import type { EmployeeLine, LineStatus } from '../types';

// A run's lines as the Employees tab shows them (DEF-0mvnmww1b001-1cc65a).
// The tab used to list calculated lines only, so an errored person appeared
// only in the transient toast after Calculate. These pure helpers decide what
// counts as pay and in what order problems are shown — kept out of JSX so the
// rule "an errored line never reads as a zero-pay success" is tested once.

const KNOWN: readonly LineStatus[] = ['calculated', 'error', 'excluded', 'pending'];

/** Wire status → LineStatus. Absent means a server from before this change,
 *  which only ever sent calculated lines; anything unrecognised is treated as
 *  NOT calculated, so it can never be shown with figures. */
export function toLineStatus(raw: unknown): LineStatus {
  if (raw === undefined || raw === null || raw === '') return 'calculated';
  return (KNOWN as readonly unknown[]).includes(raw) ? (raw as LineStatus) : 'pending';
}

/** Only a calculated line has pay figures. */
export function hasPay(line: Pick<EmployeeLine, 'status'>): boolean {
  return line.status === 'calculated';
}

const RANK: Record<LineStatus, number> = { error: 0, excluded: 1, pending: 2, calculated: 3 };

/** Problems first — errored, then excluded, then not yet calculated — then
 *  the calculated lines in the order the server sent them. Stable. */
export function orderRunLines<T extends Pick<EmployeeLine, 'status'>>(lines: readonly T[]): T[] {
  return lines
    .map((line, i) => ({ line, i }))
    .sort((a, b) => RANK[a.line.status] - RANK[b.line.status] || a.i - b.i)
    .map((x) => x.line);
}

export interface RunLineCounts {
  calculated: number;
  error: number;
  excluded: number;
  pending: number;
  /** Lines with no calculation (error + excluded + pending). */
  notCalculated: number;
}

export function countRunLines(lines: readonly Pick<EmployeeLine, 'status'>[]): RunLineCounts {
  const c: RunLineCounts = { calculated: 0, error: 0, excluded: 0, pending: 0, notCalculated: 0 };
  for (const l of lines) {
    c[l.status] += 1;
    if (l.status !== 'calculated') c.notCalculated += 1;
  }
  return c;
}
